import * as React from 'react';
import { UserContext } from '@app/Context/UserContext';
import QualityComponentHelper from '@app/Service/QualityComponentHelper';
import ReportDataHelper from '@app/Service/Report/ReportDataHelper';
import QualityComponentInterface from '@app/Interface/QualityComponentInterface';
import ItemLoader from '@app/Components/ItemLoader';
import TabulatorFormatters from '@app/utils/TabulatorFormatters';
import moment from "moment-timezone";
import { useMediaQuery } from '@react-hook/media-query';

import SCTabulator from '@app/utils/SCTabulator';
import options from '@app/utils/SCTabulatorCommonOptions';
import TabulatorAccessors from '@app/utils/TabulatorAccessors';

interface CurrentQualityReportProps {
    pageId: number,
    setLastUpdatedText: React.Dispatch<React.SetStateAction<string>>;
    setReportTableRef: React.Dispatch<React.SetStateAction<React.MutableRefObject<null> | null>>;
    setLoading: React.Dispatch<React.SetStateAction<boolean>>;
    loading: boolean;
}


const CurrentQualityReport: React.FC<CurrentQualityReportProps> = ({ pageId, setLastUpdatedText, setReportTableRef, setLoading, loading }) => {
    // In order to get tabulator to work with moment-timezone
    // we need to add moment to the global namespace.
    // see: https://stackoverflow.com/a/54135475/8038146
    window.moment = moment;

    const isSmallScreen = useMediaQuery('only screen and (max-width: 800px)');

    const { userState } = React.useContext(UserContext);

    // Update the parent report page's download CSV function to tap into our report's tabulator ref
    const tableRef = React.useCallback(node => {
        setReportTableRef({current: node});
    }, []);
    const [reportData, setReportData] = React.useState<QualityComponentInterface[]>([]);

    const leucTestThreshold = Number(process.env.THRESHOLD_LEUC_TEST) ?? 350;
    const leucTestCompareType = String(process.env.THRESHOLD_LEUC_TEST_COMPARE_TYPE) ?? "greater";

    const piTestThreshold = Number(process.env.THRESHOLD_PI_TEST) ?? 50;
    const piTestCompareType = String(process.env.THRESHOLD_PI_TEST_COMPARE_TYPE) ?? "greaterOrEqual";

    const waterTestThreshold = Number(process.env.THRESHOLD_WATER_TEST) ?? 524;
    const waterTestCompareType = String(process.env.THRESHOLD_WATER_TEST_COMPARE_TYPE) ?? "lesserOrEqual";

    const rawTestThreshold = Number(process.env.THRESHOLD_RAW_TEST) ?? 15;
    const rawTestCompareType = String(process.env.THRESHOLD_RAW_TEST_COMPARE_TYPE) ?? "greaterOrEqual";

    const formatTime = (time: number) => {
        const timeString = time.toString().padStart(4, '0'); // Ensure the time string is at least 4 characters long
        const hours = timeString.slice(0, -2);
        const minutes = timeString.slice(-2);
        return `${hours}:${minutes}`;
    };

    // TODO: Will we eventually add search? Requirements say that's a future phase.
    const tabulatorColumns = [
        {
            title: 'Date', field: 'PICKUP_DATE', formatter: 'datetime', formatterParams: {
                outputFormat: 'MMM DD',
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        {
            title: 'Time', field: 'PICKUP_TIME', formatter: (cell) => formatTime(cell.getValue())
        },
        { title: 'Tank', field: 'TANK_NUMBER', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        {
            title: 'Lbs', field: 'POUNDS', formatter: 'money', formatterParams: {
                precision: false
            }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor
        },
        { title: 'Raw', field: 'RAW_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: rawTestThreshold, compareType: rawTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'PI', field: 'PI_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: piTestThreshold, compareType: piTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SCC', field: 'LEUC_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: leucTestThreshold, compareType: leucTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'Water', field: 'WATER_TEST', formatter: TabulatorFormatters.recentResultsFormatter, formatterParams: { threshold: waterTestThreshold, compareType: waterTestCompareType }, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'AB', field: 'AB_TEST', formatter: TabulatorFormatters.abTestFormatter, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'SED', field: 'SED_TEST', accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
        { title: 'LPC', field: 'LPC_TEST', visible: true, accessorDownload: TabulatorAccessors.nullValueDownloadAccessor },
    ];

    // Set last updated text
    React.useEffect(() => {
        const controller = new AbortController();

        let mounted = true;

        ReportDataHelper
            .getTableUpdatedText(controller, 'ndaWeb_QualityComponents')
            .then(text => { if (mounted) setLastUpdatedText(text) });

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, []);

    // When the page, user, or FPU change, fetch new report data
    React.useEffect(() => {
        const controller = new AbortController();
        let mounted = true;
        async function fetchReportData() {
            if (userState.user?.currentFpuId > 0) {
                const rows = await QualityComponentHelper.getQualityComponents(controller, userState.user.currentFpuId, pageId, 'qualityComponents');
                if (mounted) {
                    setReportData(rows);
                    setLoading(false);
                }
            } else {
                if (mounted) {
                    setReportData([]);
                    setLoading(false);
                }
            }
        }

        if (userState.user.currentFpuId && pageId) {
            setLoading(true);
            fetchReportData();
        }

        return () => {
            mounted = false;
            controller?.abort();
        };

    }, [pageId, userState.user.id, userState.user.currentFpuId, setLoading]);

    if (loading) {
        return <ItemLoader />;
    }

    if (reportData.length === 0) {
        return <p><em>No report data to show for the selected producer</em></p>;
    }

    // check if we need to hide LPC column
    const hasLpcData = QualityComponentHelper.hasLpcData(reportData);

    if (hasLpcData === false) {

        // hide LPC columns
        const lpcIndex = tabulatorColumns.findIndex(element => element.field === "LPC_TEST");

        const lpcColumn = tabulatorColumns[lpcIndex];

        lpcColumn.visible = false;

    }

    return (
        <>
            <SCTabulator
                tableRef={tableRef}
                data={reportData}
                columns={tabulatorColumns}
                layout={isSmallScreen ? 'fitData' : 'fitColumns'}
                options={{...options('nda-report-current-quality-sc')}}
            />
        </>
    );
};

export default CurrentQualityReport;